import { useContext, useRef } from "react";
import { UserContext } from "@src/App";
import { likeReview, likeComment } from "@src/api/community/community";
// review
import { ReviewProviderData } from "./Provider";
import { uploadImage } from "@src/api/common";
import { updateCommunityComment, submitCommunityComment } from "@src/api/community/community";
import { getCommunityCommentList, getReviewDetail } from "@src/api/community/community";

export function useReviewActions() {
  // context
  const {
    partner,
    getCommunityList,
    getBlogList,
    getBlindList,
    communityList,
    setCommunityList,
    commentList,
    setCommentList,
    setBlogReviewList,
    setBlindList,
    showAllReview,
    blogReviewList,
    blogReviewTotal,
    setBlogReviewTotal,
    specificComment,
    reviewDataDetail,
    setReviewDataDetail,
    filter,
  } = useContext(ReviewProviderData).data;

  // user
  const user = useContext(UserContext);

  // 리뷰 패치 사이즈
  const REVIEW_FETCH_SIZE = 20;

  // 리뷰 블로그 패치 사이즈
  const REVIEW_FETCH_SIZE_FOR_BLOG = 10;

  // 리뷰 패치 시작 인덱스
  const reviewFetchStartIndex = useRef(0);

  // 리뷰 블로그 패치 시작 인덱스
  const reviewFetchStartIndexForBlog = useRef(0);

  // 리뷰 패치 중 여부
  const reviewIsFetching = useRef(false);

  // 리뷰 패치 종료 여부
  const reviewFetchEnd = useRef(false);

  // 리뷰 리스트 호출
  const getReviewList = async (callType: "init" | "add", param?: any) => {
    if (!partner) return;
    const params = param
      ? param
      : {
          // 기본대로 사용할 경우 파라미터 전달 없이 사용
          partnerUuid: partner.uuid,
          blockState: 2,
          fetchStart: reviewFetchStartIndex.current,
          fetchSize: REVIEW_FETCH_SIZE,
          sort: filter === "최신순" ? 2 : 4,
        };

    // 지점이 있을 경우 추가 처리
    const hasBranchStore = partner.reviewSummary.branchPartnerCnt > 0;
    if (hasBranchStore) {
      params.withStore = showAllReview ? 0 : 1; // 전체 리뷰 보기 여부
    }

    return getCommunityList(params).then((res) => {
      const data = res?.data?.item;
      if (data) {
        switch (callType) {
          case "init":
            setCommunityList(data);
            break;
          case "add":
            setCommunityList([...communityList, ...res.data.item]);
            break;
        }
      }
      return data ? data.length : 0;
    });
  };

  // 블라인드 리뷰 리스트 호출
  const getBlindReviewList = async (param?: any) => {
    let params = {};
    if (partner) {
      if (param) {
        params = param;
      } else {
        params = {
          partnerUuid: partner.uuid,
          blockState: 1,
          fetchStart: 0, // 0?
          fetchSize: 999,
        };
      }
    } else {
      params = {
        fetchStart: 0,
        fetchSize: 999,
        blockState: 1,
        ...param,
      };
    }
    const { data } = await getBlindList(params);
    setBlindList(data.item);
    return data.item.length;
  };

  // 블로그 리뷰 리스트 호출
  const getBlogReviewList = async (callType: "init" | "add", param?: any) => {
    if (partner) {
      if (!user?.me?.uuid) return;
      const params = param
        ? param
        : {
            // 기본대로 사용할 경우 파라미터 전달 없이 사용
            uuid: user.me.uuid,
            fetchStart: reviewFetchStartIndexForBlog.current,
            fetchSize: REVIEW_FETCH_SIZE_FOR_BLOG,
          };
      return getBlogList(params, partner.uuid).then((res) => {
        const data = res?.data?.item;
        if (data) {
          switch (callType) {
            case "init":
              setBlogReviewList(data);
              break;
            case "add":
              setBlogReviewList([...blogReviewList, ...data]);
              break;
          }
        }
        if (blogReviewTotal !== res.data.total) {
          setBlogReviewTotal(res.data.total);
        }
        return data ? data.length : 0;
      });
    }
  };

  // 좋아요 액션
  const likeAction = (communityUuid: string, state: number, itemType: "community" | "comment") => {
    if (!user?.me?.uuid) return;
    if (itemType === "comment") {
      const prevCommentList = commentList;
      // 먼저 좋아요 하고 에러나면 다시 취소 (임시)
      const newList = commentList.map((list) => {
        if (list.uuid === communityUuid) {
          list.isLike = state;
          list.likeCnt = list.likeCnt + (state ? 1 : -1);
        }
        return list;
      });
      setCommentList(newList);
      likeComment(communityUuid, state, user.me.uuid)
        .then((_) => {})
        .catch((e) => {
          setCommentList(prevCommentList);
        });
    } else {
      // 먼저 좋아요 하고 에러나면 다시 취소 (임시)
      const prevCommunityList = communityList;
      const prevDetail = reviewDataDetail;
      if (!communityList) {
        const newList = {
          ...reviewDataDetail,
          reviewData: {
            ...reviewDataDetail.reviewData,
            isLike: state,
            likeCnt: reviewDataDetail.reviewData.likeCnt + (state ? 1 : -1),
          },
        };
        setReviewDataDetail(newList);
      } else {
        const newList = communityList.map((list) => {
          if (list.uuid === communityUuid) {
            list.isLike = state;
            list.likeCnt = list.likeCnt + (state ? 1 : -1);
          }
          return list;
        });
        setCommunityList(newList);
      }
      likeReview(communityUuid, state, user.me.uuid)
        .then((_) => {})
        .catch((e) => {
          if (!communityList) {
            setReviewDataDetail(prevDetail);
          } else {
            setCommunityList(prevCommunityList);
          }
        });
    }
  };

  /**
   *
   * 해당 함수로 리뷰 리스트 호출
   * @param reviewType 호출 이미지 타입 (community: 커뮤니티, blog: 블로그)
   * @param calltype 호출 타입 (init: 초기 호출, add: 추가 호출)
   * @param param 기본방식 대로 사용할 경우 파라미터 전달 없이 사용, 별도 파라미터 전달 필요한 경우 파라미터 넘겨 사용
   */
  const reviewListCall = async (
    reviewType: "community" | "blog",
    calltype: "init" | "add",
    param?: any,
  ) => {
    reviewIsFetching.current = true;
    switch (reviewType) {
      case "community":
        if (calltype === "init") {
          reviewFetchEnd.current = false;
          reviewFetchStartIndex.current = 0;
        }
        getReviewList(calltype, param)
          .then((res) => {
            if (res === 0) {
              reviewFetchEnd.current = true;
              // console.log(reviewFetchStartIndex.current);
              return;
            }
            if (calltype === "init") reviewFetchStartIndex.current = REVIEW_FETCH_SIZE;
            else reviewFetchStartIndex.current += REVIEW_FETCH_SIZE;
          })
          .finally(() => {
            reviewIsFetching.current = false;
          });
        break;
      case "blog":
        if (calltype === "init") {
          reviewFetchEnd.current = false;
          reviewFetchStartIndexForBlog.current = 0;
        }
        getBlogReviewList(calltype, param)
          .then((res) => {
            if (res === 0) {
              reviewFetchEnd.current = true;
              return;
            }
            if (calltype === "init")
              reviewFetchStartIndexForBlog.current = REVIEW_FETCH_SIZE_FOR_BLOG;
            else reviewFetchStartIndexForBlog.current += REVIEW_FETCH_SIZE_FOR_BLOG;
          })
          .finally(() => {
            reviewIsFetching.current = false;
          });
        break;
      default:
        break;
    }
  };

  // 이미지 업로드 후 댓글 작성
  const sendCommentWithImage = async (
    param: { content: string; secret: 0 | 1; uuid: string; urls?: string; anonymous?: string },
    file: File,
    type: "submit" | "reply" | "modify",
  ) => {
    if (!user?.me || !file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imgSrc = await uploadImage(formData);
      param["urls"] = imgSrc.data.data;
      sendComment(param, type);
    } catch (err) {
      alert("이미지 업로드에 실패하였습니다. 다시 시도해주세요.");
    }
  };

  // 댓글 작성
  const sendComment = async (
    param: { content: string; secret: 0 | 1; uuid: string; urls?: string; anonymous?: string },
    type: "submit" | "reply" | "modify",
  ) => {
    // 댓글 결과에 따른 ui 업데이트를 프론트에서 치리 중이나 데이터 일관성/최신상태 위해서 변경해야할 수 있음
    switch (type) {
      case "submit":
      case "reply":
        try {
          const res = await submitCommunityComment(param);
          if (res?.data?.item) {
            if (type === "reply") {
              const replyComment = res.data.item;
              const newComments = [...commentList];
              let insertIndex = -1;
              // upperComment부터 찾아서 가장 빠른.. 뒤에 있는 원댓글 직전에 붙임
              const startIndex = commentList.findIndex(
                (comment) => comment.id === specificComment.commentData.id,
              );
              for (let i = startIndex + 1; i < commentList.length; i++) {
                if (commentList[i].depth === 1) {
                  insertIndex = i - 1;
                  break;
                }
              }
              // 뒤에 원댓글이 더 없는 경우 리스트 맨 뒤에 붙임
              if (insertIndex === -1) {
                insertIndex = commentList.length - 1;
              }
              newComments.splice(insertIndex + 1, 0, replyComment);
              setCommentList(newComments);
            } else {
              setCommentList([...commentList, res.data.item]);
            }
          }
        } catch (err) {
          alert("댓글 작성에 실패했습니다. 잠시 후 다시 시도해주세요.");
        }
        break;
      case "modify":
        try {
          const res = await updateCommunityComment(specificComment.commentData.uuid, param);
          if (res?.data?.item) {
            const newComment = commentList.map((comment) => {
              if (comment.id === specificComment.commentData.id) {
                return res.data.item;
              }
              return comment;
            });
            setCommentList(newComment);
          }
        } catch (err) {
          alert("댓글 수정에 실패했습니다. 잠시 후 다시 시도해주세요.");
        }
        break;
    }
  };

  // 댓글 작성 버튼 클릭
  const writeComment = async ({
    param,
    file,
    type,
  }: {
    param: { content: string; secret: 0 | 1; uuid: string; urls?: string; anonymous?: string };
    file: File;
    type?: "submit" | "reply" | "modify";
  }) => {
    if (!user?.me) return;
    if (!param.content || (param.content && param.content.trim().length === 0)) return;
    if (file) {
      sendCommentWithImage(param, file, type);
    } else {
      sendComment(param, type);
    }
  };

  // 리뷰 상세 호출
  const reviewDetailCall = async (reviewUUid: string) => {
    return getReviewDetail({ uuid: user.me.uuid }, reviewUUid)
      .then((res) => {
        if (res?.data?.item) {
          return res.data.item;
        } else {
          return null;
        }
      })
      .catch((e) => {
        throw e;
      });
  };

  // 코멘트 리스트 호출
  const reviewCommentCall = async (reviewUUid: string) => {
    const param = {
      communityUuid: reviewUUid, //리뷰 uuid
      uuid: user?.me?.uuid ? user.me.uuid : null, //고객 uuid
    };
    return getCommunityCommentList(param)
      .then((res) => {
        setCommentList(res.data.item);
        return "success";
      })
      .catch((e) => {
        throw e;
      });
  };

  return {
    likeAction,
    getReviewList,
    getBlogReviewList,
    getBlindReviewList,
    reviewListCall,
    writeComment,
    reviewDetailCall,
    reviewCommentCall,

    REVIEW_FETCH_SIZE,
    REVIEW_FETCH_SIZE_FOR_BLOG: REVIEW_FETCH_SIZE_FOR_BLOG,
    REVIEW_FETCH_START_INDEX: reviewFetchStartIndex.current,
    REVIEW_FETCH_START_INDEX_FOR_BLOG: reviewFetchStartIndexForBlog.current,
    REVIEW_IS_FETCHING: reviewIsFetching.current,
    REVIEW_IS_FETCH_END: reviewFetchEnd.current,
  };
}
